import React, { FunctionComponent, useContext } from 'react'
import { StyleSheet, Text } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { HyperLinkWithStyle } from '../../atoms/link/HyperLinkWithStyle'
import { AppContext } from '../../context'
import { getTypeStyles } from '../../styles/typeStyles'
import { openUrl } from '../../utils/expo-linking'
import { BodyText } from '../bodyText/BodyText'
import { Link } from '../link/Link'

type ChatBubbleTextProps = {
  text: string
  fontColor?: string
  onLinkPress?: (link: string) => void
}

const styles = StyleSheet.create({
  text: {
    textAlign: 'left',
    flexShrink: 1,
    flexWrap: 'wrap',
    lineHeight: 24,
  },
})

const HyperLinkContainer = styled(HyperLinkWithStyle)(({ theme }) => ({
  margin: theme.spacing['12px'],
}))

/**
 * Simple Chat bubble text styling
 */
export const ChatBubbleText: FunctionComponent<ChatBubbleTextProps> = ({ text, fontColor, onLinkPress }) => {
  const { copyPasteDisabled } = useContext(AppContext)
  const { colors, fontFamilyMetadata } = useTheme()
  const style = { ...getTypeStyles(colors, fontFamilyMetadata).bodyDefault, ...styles.text, color: fontColor }

  /**
   * Tokenizes text into plain text, links, and bold segments in one pass
   * @param text - The input text containing markdown links and bold
   * @returns Array of tokens with type 'text', 'link', or 'bold'
   */
  const tokenizeMarkdownAll = (text: string) => {
    // Combined regex for links and bold (italics removed)
    // Links: [text](url)
    // Bold: **text** or __text__
    const markdownRegex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|__([^_]+)__)/g

    const tokens: Array<{
      type: 'text' | 'link' | 'bold'
      content: string
      url?: string
      linkText?: string
      displayText?: string
    }> = []

    let lastIndex = 0
    let match

    while ((match = markdownRegex.exec(text)) !== null) {
      // Add plain text before the current match (if any)
      if (match.index > lastIndex) {
        const plainText = text.slice(lastIndex, match.index)
        if (plainText) {
          tokens.push({ type: 'text', content: plainText })
        }
      }

      // Determine the type and extract the relevant data
      if (match[2] && match[3]) {
        // Link: [text](url)
        tokens.push({
          type: 'link',
          content: match[0], // Full markdown syntax [text](url)
          linkText: match[2], // The display text
          url: match[3], // The URL
        })
      } else if (match[4] || match[5]) {
        // Bold: **text** or __text__
        tokens.push({
          type: 'bold',
          content: match[0], // Full markdown syntax
          displayText: match[4] || match[5],
        })
      }

      lastIndex = match.index + match[0].length
    }

    // Add remaining plain text after the last match (if any)
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex)
      if (remainingText) {
        tokens.push({ type: 'text', content: remainingText })
      }
    }

    // If no markdown was found, return the entire text as a single text token
    if (tokens.length === 0) {
      tokens.push({ type: 'text', content: text })
    }

    return tokens
  }

  const tokens = tokenizeMarkdownAll(text)
  const tokensConcatenatedWithAllMarkdown = tokens.map((token) => {
    if (token.type === 'text') {
      return token.content
    } else if (token.type === 'link') {
      return (
        <Link
          key={token.content}
          text={token.linkText}
          onPress={() => openUrl(token.url ?? '')}
          underline
          color={fontColor}
        />
      )
    } else if (token.type === 'bold') {
      return (
        <BodyText key={token.content} style={{ color: fontColor, fontWeight: 'bold' }}>
          {token.displayText}
        </BodyText>
      )
    } else {
      // italics
      return (
        <BodyText key={token.content} style={{ color: fontColor, fontStyle: 'italic' }}>
          {token.displayText}
        </BodyText>
      )
    }
  })

  return (
    <HyperLinkContainer textColor={fontColor} onLinkPress={onLinkPress}>
      {/* Required to be Text component for Hyperlink to work correctly */}
      {/* eslint-disable-next-line lyrahealth/no-react-native-text */}
      <Text selectable={!copyPasteDisabled} style={style}>
        {tokensConcatenatedWithAllMarkdown}
      </Text>
    </HyperLinkContainer>
  )
}
